import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import cx from "classnames";
/** BEFORE - without useMemo: import React, { useEffect, useState } from "react"; */
/** BEFORE - without useContext: import React, { useEffect, useMemo, useState } from "react"; */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useController, useFormContext, useFormState } from "react-hook-form";
import {
  useGetDocumentLabel,
  useGetFieldLabel,
  useGetFieldPlaceholder,
} from "../../contexts/LanguageContext";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useUploadFile } from "../../hooks/applications.hooks";
import { useMediaQuery } from "../../hooks/common";
import { TDocumentConfig } from "../../hooks/forms.hooks";
import { VALIDATORS } from "../../utils/validators";
import { getFieldCharacterLimit } from "../../config/fieldCharacterLimits";
import { FieldCharacterCounter } from "./FieldCharacterCounter";
/**
 * BEFORE - without the Offline Tentative list and helper:
 * import { PERMANENT_ONLY_DOCUMENT_IDS, RL_APPLICATION_TYPE_OPTION_ID, RL_APPLICATION_TYPE } from "../../config/phaseConstants";
 */
import {
  PERMANENT_ONLY_DOCUMENT_IDS,
  OFFLINE_TENTATIVE_ONLY_DOCUMENT_IDS,
  RL_APPLICATION_TYPE_OPTION_ID,
  RL_APPLICATION_TYPE,
  isOfflineTentativeTypeValue,
  rlApplicationTypeOptionId,
  sortByDocumentDisplayOrder,
} from "../../config/phaseConstants";
import { branchFeatureFlags } from "../../config/branchFeatureFlags";
import { ROAD_WIDTH_FIELD_UID, isOldRoadWidth, roadWidthText } from "../../config/roadWidthOptions";
import { matchSelectOption } from "../../utils/matchSelectOption";
import { longApplicationTypeOptionName } from "../../config/applicationTypeLabels";
import { RoadWidthNote, RoadWidthNoteContext } from "../RoadWidthNote/RoadWidthNote";
import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  DeleteButton,
  ViewButton,
} from "../CommonComponents/EditComponent/EditComponent";
import Input, { InputProps, TextArea } from "../CommonComponents/Input/Input";
import Select from "../CommonComponents/Select/Select";
import { ErrorToast } from "../toastMessage";
import { _axios } from "../../utils/axios";
import { useMoujaConfigs, getKhasaraRangeForMouja } from "../../hooks/mouja-khasara";
import APP_CONFIG from "../../config/appConfig";
type PropTypes = {
  disabled?: boolean;
  validator?: string;
  field: TApplicationField & {
    options?: TApplicationFieldOption[];
  };
};

function RenderInput({ validator, field, disabled }: PropTypes) {
  const { register, setValue } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  const isPlotNo = field.uid === "plot_no" || field.uid === "plot_number" || field.name === "Plot No";
  const isLayoutName =
    field.uid === "unauthorized_layout_name" ||
    field.uid === "layout_name" ||
    field.uid === "unauthorizedLayoutName" ||
    field.name === "Unauthorized Layout Name";

  const isExistingRoadWidth = 
    field.name === "Existing Road width approaching to plot" || 
    field.name === "Existing Road width approaching to plot " || 
    field.uid === "existing_road_width_approaching_to_plot" ||
    field.uid === "existing_road_width";

  let props: InputProps = {};
  let registerOpts: Parameters<typeof register>[1] = undefined;

  switch (field.type) {
    case "integer":
    case "area":
    case "float": {
      const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (
        e
      ) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

      props = { type: "number", onKeyDown: blockInvalidChar };
      registerOpts = {
        valueAsNumber: true,
        min: { value: 0, message: "Cannot be less than 0" },
      };
      break;
    }
    case "date": {
      props = { type: "date" };
      registerOpts = { valueAsDate: true };
      break;
    }
  }
  /**
   * A field with a character limit (Address) stops accepting characters at
   * the limit and shows a counter inside its box, at the right end. The validation rule is
   * there for a value that is already longer when the form opens, which
   * maxLength on the input cannot catch.
   *
   * BEFORE - the input was returned on its own, with no limit:
   *   return (
   *     <Input
   *       label={convert(field)}
   *       ...
   *       {...register(field.uid, {
   *         required: field.mandatory ? "* Required" : false,
   */
  const characterLimit = getFieldCharacterLimit(field.uid);

  /**
   * BEFORE - the counter was a line above the label:
   *   {characterLimit !== undefined && (
   *     <FieldCharacterCounter fieldUid={field.uid} limit={characterLimit} />
   *   )}
   * It is now passed as `trailing`, so it sits inside the input box, at its
   * right end.
   */
  return (
    <>
    <Input
      label={convert(field)}
      {...(characterLimit !== undefined && {
        trailing: <FieldCharacterCounter fieldUid={field.uid} limit={characterLimit} />,
      })}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      placeholder={convertPlaceholder(field)}
      disabled={disabled ?? false}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...props}
      {...(characterLimit !== undefined && { maxLength: characterLimit })}
      {...register(field.uid, {
        required: field.mandatory ? "* Required" : false,
        ...(characterLimit !== undefined && {
          maxLength: {
            value: characterLimit,
            message: `Cannot be longer than ${characterLimit} characters`,
          },
        }),
        ...(isPlotNo && {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            /** Plot numbers can have letters, e.g. 12A. BEFORE: val.replace(/[^0-9]/g, "") (digits only). */
            let cleanVal = val.replace(/[^a-zA-Z0-9]/g, "");
            cleanVal = cleanVal.replace(/^0+(?=\d)/, "");
            setValue(field.uid, cleanVal, { shouldValidate: true });
          },
        }),
        ...(isLayoutName && {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            const cleanVal = val.replace(/[^a-zA-Z0-9 ]/g, "");
            setValue(field.uid, cleanVal, { shouldValidate: true });
          },
        }),
        ...(isExistingRoadWidth && {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            // Only allow numbers and at most one decimal point
            const cleanVal = val.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");
            setValue(field.uid, cleanVal, { shouldValidate: true });
          },
        }),
        ...(!!validator &&
          !!VALIDATORS[validator] && { validate: VALIDATORS[validator] }),
        ...registerOpts,
      })}
    />
    </>
  );
}

function RenderArea({ field, disabled }: PropTypes) {
  const { register, getValues } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  let options = ["sq. mt.", "sq. ft."];

  const unitName = `unit_${field.uid}`;

  const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (e) =>
    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  const props = { type: "number", onKeyDown: blockInvalidChar };
  const registerOpts = { valueAsNumber: true };

  return (
    <div className="flex items-end justify-between gap-4">
      <Input
        label={convert(field)}
        containerClass="basis-2/4 md:basis-3/4"
        required={!!field?.mandatory}
        error={!!info?.errors?.[field.uid]}
        placeholder={convertPlaceholder(field)}
        disabled={disabled ?? false}
        helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
        {...props}
        {...register(field.uid, {
          required: field.mandatory ? "* Required" : false,
          ...registerOpts,
        })}
      />
      {/*
       <Select
        containerClass="basis-2/4 md:basis-1/4"
        error={!!info?.errors?.[unitName]}
        disabled={disabled}
        required={!!field?.mandatory}
        defaultValue={""}
        helperText={info?.errors?.[unitName]?.message?.toString?.() || ""}
        {...register(unitName, {
          validate: (v) => {
            if (getValues(field.name) && !v) return "* Required";
            return true;
          },
        })}
      >
        <option hidden></option>
        {options?.map((e) => (
          <option key={`${field.id}-${e}`} value={e}>
            {e}
          </option>
        ))}
      </Select>
      */}
      <Input
        containerClass="basis-2/4 md:basis-1/4"
        value="sq. mt."
        disabled={true}
        readOnly={true}
        {...register(unitName, { value: "sq. mt." })}
      />
    </div>
  );
}

function RenderSelect({ field, disabled }: PropTypes) {
  const { register, getValues, setValue } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  const [dbMoujaOptions, setDbMoujaOptions] = useState<any[]>([]);

  const isMaujaField = field.uid === "mauja" || field.name === "Mauja" || String(field.uid).toLowerCase().includes("mauje");

  const isConstructionField = field.uid === "is_there_a_construction";
  const enableConstructionField = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION);
  const shouldDisableConstruction = isConstructionField && !enableConstructionField;

  useEffect(() => {
    if (shouldDisableConstruction) {
      setValue(field.uid, false, { shouldValidate: true });
    }
  }, [shouldDisableConstruction, field.uid, setValue]);

  useEffect(() => {
    if (isMaujaField) {
      _axios
        .get("/admin/mouja-asr-config")
        .then((res: any) => {
          if (res.data && res.data.data && Array.isArray(res.data.data)) {
            const mapped = res.data.data.map((m: any) => ({
              id: m.mouja_name,
              name: m.mouja_name,
              active: true,
            }));
            setDbMoujaOptions(mapped);
          }
        })
        .catch((err: any) => console.error("Error fetching Mauza options:", err));
    }
  }, [isMaujaField]);

  /**
   * Memoised so the list keeps the same identity between renders; the
   * option-matching effect below depends on `options` and would otherwise
   * re-run on every render for this dropdown.
   */
  const applicationTypeOptions = useMemo(() => {
    const isOfflineTentativeOption = (opt: TApplicationFieldOption) =>
      String(opt.id) === String(RL_APPLICATION_TYPE_OPTION_ID.OFFLINE_TENTATIVE);
    /**
     * Permanent and Tentative are listed by their long names, which say who
     * each type is for (config/applicationTypeLabels.ts); the option ids,
     * and so the saved value, are unchanged.
     * BEFORE: const allOptions = field?.options || [];
     */
    const allOptions = (field?.options || []).map((opt) => ({
      ...opt,
      name: longApplicationTypeOptionName(opt.name),
    }));
    const otherOptions = allOptions.filter((opt) => !isOfflineTentativeOption(opt));
    if (!branchFeatureFlags.ENABLE_OFFLINE_TENTATIVE_RL) return otherOptions;
    return [...otherOptions, ...allOptions.filter(isOfflineTentativeOption)];
  }, [field?.options]);

  let options: { id: number | boolean | string; name: string; active?: boolean }[] =
    field.type === "boolean"
      ? [
        { id: true, name: "Yes", active: true },
        { id: false, name: "No", active: true },
      ]
      : isMaujaField
        ? dbMoujaOptions
        : field?.options || [];

  /**
   * The Application Type dropdown gets its own option list: the "Offline
   * Tentative RL" option is moved to the end (its fixed id, 9990, is lower
   * than Permanent's and Tentative's, so it would otherwise be listed first),
   * and it is left out altogether while its feature flag is off, so the type
   * cannot be chosen even if the option row already exists in the database.
   */
  if (field.uid === "rl_application_type") {
    options = applicationTypeOptions;
  }

  /**
   * Road width: an application can hold an old value ("50", "20 FOOT",
   * "No") that is not one of the four widths (config/roadWidthOptions.ts).
   * The value it opened with is kept for the note. On the Resubmit page
   * the field was returned to be corrected, so the old value is cleared and
   * only the four widths are listed; everywhere else the old value is listed
   * as "<value> (old value)" and stays selected, so a reviewer can save other
   * fields without choosing a width.
   */
  const roadWidthPage = useContext(RoadWidthNoteContext);
  const isRoadWidth = field.uid === ROAD_WIDTH_FIELD_UID;
  const [openingRoadWidth] = useState<unknown>(() =>
    isRoadWidth ? getValues(field.uid) : undefined
  );
  const replaceOldRoadWidth =
    isRoadWidth && roadWidthPage === "resubmit" && isOldRoadWidth(openingRoadWidth);
  const currentRoadWidth = isRoadWidth ? getValues(field.uid) : undefined;
  const oldRoadWidthOption = useMemo(() => {
    if (!isRoadWidth || replaceOldRoadWidth || !isOldRoadWidth(currentRoadWidth)) return null;
    const stored = String(currentRoadWidth).trim();
    const listed = (field?.options || []).some(
      (opt) =>
        String(opt.id) === stored || String(opt.name).trim().toLowerCase() === stored.toLowerCase()
    );
    if (listed) return null;
    return { id: stored, name: `${roadWidthText(stored)} (old value)`, active: true };
  }, [isRoadWidth, replaceOldRoadWidth, currentRoadWidth, field?.options]);
  /** Memoised so the option-matching effect below does not re-run on every render. */
  const roadWidthOptions = useMemo(
    () => (oldRoadWidthOption ? [oldRoadWidthOption, ...(field?.options || [])] : null),
    [oldRoadWidthOption, field?.options]
  );
  if (roadWidthOptions) {
    options = roadWidthOptions;
  }

  let registerOpts: Parameters<typeof register>[1] = undefined;
  const optionsAreNumeric =
    options.length > 0 && options.every((o) => o.id !== "" && !isNaN(Number(o.id)));
  console.log("getValues", getValues())

  // 🌐 PERMANENT DYNAMIC SOLUTION: Works for ALL present & future dropdown fields!
  const rawValue = getValues(field.uid);

  useEffect(() => {
    if (shouldDisableConstruction) return;
    if (field.type === "boolean" && (rawValue === 1 || rawValue === "1" || rawValue === 0 || rawValue === "0")) {
      const boolVal = rawValue === 1 || rawValue === "1";
      setValue(field.uid, boolVal, { shouldValidate: true });
      return;
    }
    if (rawValue !== undefined && rawValue !== null && rawValue !== "" && options?.length > 0) {
      /**
       * A saved application stores its Application Type as "Permanent",
       * "Temporary" or "Offline Tentative", which the matching below cannot
       * tie to the "Tentative RL" option, so the stored type is turned into
       * its option id first.
       * BEFORE: const valStr = String(rawValue).trim().toLowerCase();
       */
      const storedTypeOptionId =
        field.uid === "rl_application_type" ? rlApplicationTypeOptionId(rawValue) : null;
      const valStr = String(storedTypeOptionId ?? rawValue).trim().toLowerCase();

      /**
       * An option whose id or name is exactly the stored value wins; only
       * when there is none is a partial name match used. Checking both in
       * one pass picked the first option whose name merely overlapped: a
       * road width saved as "> 9 m" contains "9 m", so it opened with "9 m"
       * selected.
       *
       * BEFORE - one pass with the partial match:
       * const matched = options.find((opt) => {
       *   const optIdStr = String(opt.id ?? "").trim().toLowerCase();
       *   const optNameStr = String(opt.name ?? "").trim().toLowerCase();
       *   return (
       *     optIdStr === valStr ||
       *     optNameStr === valStr ||
       *     (valStr.length > 2 && optNameStr.includes(valStr)) ||
       *     (optNameStr.length > 2 && valStr.includes(optNameStr))
       *   );
       * });
       */
      /**
       * The matching lives in utils/matchSelectOption.ts so the reviewer's
       * "Your changes" list (utils/reviewerEditChanges.ts) resolves a stored
       * value to the same option this dropdown picks.
       *
       * BEFORE - the same two passes written inline:
       * const optionText = (opt: (typeof options)[number]) => ({
       *   id: String(opt.id ?? "").trim().toLowerCase(),
       *   name: String(opt.name ?? "").trim().toLowerCase(),
       * });
       * const matched =
       *   options.find((opt) => {
       *     const { id, name } = optionText(opt);
       *     return id === valStr || name === valStr;
       *   }) ||
       *   options.find((opt) => {
       *     const { name } = optionText(opt);
       *     return (
       *       (valStr.length > 2 && name.includes(valStr)) ||
       *       (name.length > 2 && valStr.includes(name))
       *     );
       *   });
       */
      const matched = matchSelectOption(options, valStr);

      if (matched) {
        setValue(field.uid, String(matched.id), { shouldValidate: false });
      }
    }
  }, [rawValue, options, field.uid, field.type, setValue, shouldDisableConstruction]);

  /**
   * On the Resubmit page an old road width is cleared once, after the
   * matching above, so the applicant has to choose one of the four widths.
   */
  useEffect(() => {
    if (replaceOldRoadWidth) setValue(field.uid, "", { shouldValidate: false });
  }, [replaceOldRoadWidth, field.uid, setValue]);




  const select = (
    <Select
      label={convert(field)}
      error={!!info?.errors?.[field.uid]}
      // placeholder={convertPlaceholder(field)}
      disabled={field.uid === "name_of_service" || shouldDisableConstruction || disabled}
      required={!!field?.mandatory}
      value={rawValue !== undefined && rawValue !== null ? String(rawValue) : ""}
      {...register(field.uid, {
        ...(shouldDisableConstruction && { value: false }),
        ...(isMaujaField && {
          onChange: (e: any) => {
            const val = e.target.value;
            setValue("mauja", val, { shouldValidate: true });
          },
        }),
        ...(field.type === "boolean"
          ? {
            validate: (v) => {
              if (v === true || v === false || v === 1 || v === 0 || v === "1" || v === "0" || !field.mandatory) return true;
              return "* Required";
            },
            setValueAs: (v) => {
              if (v === true || v === "true" || v === 1 || v === "1") return true;
              if (v === false || v === "false" || v === 0 || v === "0") return false;
              return "";
            },
          }
          : isMaujaField || isRoadWidth
            ? {
              /**
               * The road width is sent as text, not as a number: an old
               * value such as "20 FOOT" has to go back unchanged, and the
               * backend turns an option id ("9980") into its width either way.
               * BEFORE: the road width took the valueAsNumber branch below.
               */
              required: field.mandatory ? "* Required" : false,
            }
            : {
              valueAsNumber: true,
              required: field.mandatory ? "* Required" : false,
            }),
      })}
    >
      <option value="" disabled hidden>
        {convertPlaceholder(field) || "Select Option"}
      </option>
      {/*
        An option the Admin has switched off is still listed when it is the
        value this application already holds, so a draft of a disabled
        application type opens with its type and can be completed.
        Before:
        ?.filter((e) => e.active)
      */}
      {/*
        On the Resubmit page a returned road width lists only the four
        widths, never a switched-off Yes / No.
      */}
      {options
        ?.filter(
          (e) => e.active || (!replaceOldRoadWidth && String(e.id) === String(rawValue))
        )
        ?.map((e) => (
          <option key={`${field.id}-${e.id}`} value={String(e.id)}>
            {e.name}
          </option>
        ))}
    </Select>
  );

  /**
   * BEFORE - the dropdown was returned on its own:
   *   return (<Select ...>...</Select>);
   * The road width also gets the old-value note on the Edit Application and
   * Resubmit pages.
   */
  if (isRoadWidth && roadWidthPage) {
    return (
      <div>
        {select}
        <RoadWidthNote
          value={openingRoadWidth}
          variant={roadWidthPage === "resubmit" ? "resubmit" : "edit"}
          className="mt-2"
        />
      </div>
    );
  }
  return select;
}

function
  RenderTextarea({ validator, field, disabled }: PropTypes) {
  const { register } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  /**
   * Same character-limit handling as RenderInput above, for a limited field
   * that is configured as a textarea.
   *
   * BEFORE - the textarea was returned on its own, with no limit:
   *   return (
   *     <TextArea
   *       label={convert(field)}
   *       ...
   *       {...register(field.uid, {
   *         required: field.mandatory ? "* Required" : false,
   */
  const characterLimit = getFieldCharacterLimit(field.uid);

  /**
   * BEFORE - the counter was a line above the label (as in RenderInput). It
   * is now passed as `trailing`, so it sits in the textarea's bottom-right
   * corner.
   */
  return (
    <>
    <TextArea
      label={convert(field)}
      {...(characterLimit !== undefined && {
        trailing: <FieldCharacterCounter fieldUid={field.uid} limit={characterLimit} />,
      })}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      placeholder={convertPlaceholder(field)}
      disabled={disabled ?? false}
      rows={4}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...(characterLimit !== undefined && { maxLength: characterLimit })}
      {...register(field.uid, {
        required: field.mandatory ? "* Required" : false,
        ...(characterLimit !== undefined && {
          maxLength: {
            value: characterLimit,
            message: `Cannot be longer than ${characterLimit} characters`,
          },
        }),
        ...(!!validator &&
          !!VALIDATORS[validator] && { validate: VALIDATORS[validator] }),
      })}
    />
    </>
  );
}

// Shared by RenderKhasaraSelect and RenderKhasaraManualInput so they can be
// rendered as two independent grid cells (see MaujaKhasaraPlotBlock in
// RenderStep.tsx) while staying in sync -- both read/write the same RHF
// fields (manual_khasara_no, main_khasara_no, the combined khasara uid)
// reactively via watch(), so neither owns a local copy that could drift
// out of sync with the other.
function useKhasaraFieldState(fieldUid: string) {
  const { setValue, watch } = useFormContext();
  const moujaConfigs = useMoujaConfigs();

  const selectedMauja = watch("mauja");
  const { minLimit, maxLimit, maujaDisplayName, khasaraOptions } =
    getKhasaraRangeForMouja(moujaConfigs, selectedMauja);

  const manualKhasara = watch("manual_khasara_no") || watch(fieldUid)?.split("/")?.slice(1)?.join("/") || "";
  const mainKhasaraVal = watch(`${fieldUid}_main`) || watch(fieldUid)?.split("/")?.[0] || "";

  const handleKhasaraCombine = (mainVal: string, manualVal: string) => {
    const cleanManual = manualVal.trim();
    setValue("manual_khasara_no", cleanManual);
    setValue("main_khasara_no", mainVal);

    if (mainVal && cleanManual) {
      setValue(fieldUid, `${mainVal}/${cleanManual}`, { shouldValidate: true });
    } else if (mainVal) {
      setValue(fieldUid, mainVal, { shouldValidate: true });
    } else if (cleanManual) {
      setValue(fieldUid, cleanManual, { shouldValidate: true });
    }
  };

  useEffect(() => {
    if (mainKhasaraVal && !watch("main_khasara_no")) {
      setValue("main_khasara_no", mainKhasaraVal);
    }
  }, [mainKhasaraVal]);

  return {
    selectedMauja,
    minLimit,
    maxLimit,
    maujaDisplayName,
    khasaraOptions,
    manualKhasara,
    mainKhasaraVal,
    handleKhasaraCombine,
  };
}

export function RenderKhasaraSelect({ field, disabled }: PropTypes) {
  const convert = useGetFieldLabel();
  const info = useFormState({ name: field.uid });
  const { setValue, register } = useFormContext();

  /**
   * The dropdown writes its value with setValue, so only the required rule
   * and the ref are taken from register; the ref lets Next focus the field
   * when it is empty, like the other required fields.
   */
  const { ref: khasaraRef } = register(field.uid, {
    required: field?.mandatory ? "* Required" : false,
  });

  const {
    selectedMauja,
    minLimit,
    maxLimit,
    maujaDisplayName,
    khasaraOptions,
    manualKhasara,
    mainKhasaraVal,
    handleKhasaraCombine,
  } = useKhasaraFieldState(field.uid);

  return (
    <Select
      ref={khasaraRef}
      label={`${convert(field)} (Select Main Khasara)`}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      disabled={disabled || !selectedMauja}
      helperText={!selectedMauja ? "Please select Mauza first" : info?.errors?.[field.uid]?.message?.toString?.() || ""}
      value={mainKhasaraVal}
      onChange={(e: any) => {
        const val = e.target.value;
        setValue(`${field.uid}_main`, val);
        handleKhasaraCombine(val, manualKhasara);
      }}
    >
      <option value="" disabled hidden>
        {selectedMauja ? `Select Khasara (${minLimit} to ${maxLimit})` : "Please select Mauza first"}
      </option>
      {khasaraOptions.map((num) => (
        <option key={`khasara-${num}`} value={String(num)}>
          Khasara No. {num} ({maujaDisplayName})
        </option>
      ))}
    </Select>
  );
}

export function RenderKhasaraManualInput({
  khasaraFieldUid,
  disabled,
  field,
}: {
  khasaraFieldUid: string;
  disabled?: boolean;
  /**
   * The "manual_khasara_no" field record (cp_form_fields id 114), passed
   * in by the caller so its label can go through translation. Optional
   * because this component predates that field existing in the backend at
   * all, so it still needs to work with a plain hardcoded label if no
   * field record is available for whatever reason.
   */
  field?: { label?: string; labelHindi?: string; labelMarathi?: string };
}) {
  const { manualKhasara, mainKhasaraVal, handleKhasaraCombine } =
    useKhasaraFieldState(khasaraFieldUid);
  const convert = useGetFieldLabel();

  return (
    <Input
      label={field ? convert({ ...field, label: field.label || "Manual Sub-Khasara (e.g. 55/65)" }) : "Manual Sub-Khasara (e.g. 55/65)"}
      placeholder="Enter manual sub-khasara text"
      value={manualKhasara}
      disabled={disabled ?? false}
      onChange={(e) => {
        const val = e.target.value.replace(/[^0-9\/a-zA-Z]/g, "");
        handleKhasaraCombine(mainKhasaraVal, val);
      }}
      onBlur={() => handleKhasaraCombine(mainKhasaraVal, manualKhasara)}
    />
  );
}

export function RenderField({ field, disabled }: PropTypes) {
  const { watch } = useFormContext();
  const forceReadonly =
    field.uid === "type_of_application_submitter" ||
    field.uid === "submitted_by";
  /**
   * A field the server marks `readOnly` is shown with its value but cannot
   * be changed, for example Application Type on the reviewer's Edit
   * Application page.
   * BEFORE: const isDisabled = disabled || forceReadonly;
   */
  const isDisabled = disabled || forceReadonly || !!field.readOnly;

  const enableConstructionQuestion = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION);
  const isConstructionAreaField = field.uid === "construction_area" || field.uid === "built_up_area";
  const constructionValue = watch("is_there_a_construction");

  // 🔒 Hide construction_area when feature flag is disabled OR construction is No (false)
  if (isConstructionAreaField) {
    if (
      !enableConstructionQuestion ||
      constructionValue === false ||
      constructionValue === 0 ||
      constructionValue === "0" ||
      constructionValue === "false"
    ) {
      return null;
    }
  }

  if (field.uid === "khasara_no") {
    return <RenderKhasaraSelect field={field} disabled={isDisabled} />;
  }

  // main_khasara_no is never a standalone input -- it's just the shadow RHF
  // field that khasara_no's RenderKhasaraSelect writes the main portion into
  // (see useKhasaraFieldState). Rendering it here would just duplicate the
  // khasara_no dropdown wherever both are mapped to the same form.
  if (field.uid === "main_khasara_no") {
    return null;
  }

  /**
   * Fallback only: normally Manual Sub-Khasara is rendered directly by
   * MaujaKhasaraPlotBlock (RenderStep.tsx) as part of the Mauja/Khasara/
   * Plot No. block. This branch only fires when a service is missing that
   * full trio, in which case RenderStep injects either the real
   * "manual_khasara_no" field (cp_form_fields id 114) or a synthetic
   * stand-in copied from khasara_no - either way, `field` here carries
   * whatever label/labelHindi/labelMarathi that entry has, so it's passed
   * straight through for translation.
   */
  if (field.uid === "manual_khasara_no") {
    return <RenderKhasaraManualInput khasaraFieldUid="khasara_no" disabled={isDisabled} field={field} />;
  }

  switch (field.type) {
    case "textarea":
      return (
        <RenderTextarea
          validator={field.validator}
          disabled={isDisabled}
          field={field}
        />
      );
    case "boolean":
    case "configurable-select":
    case "select":
      return <RenderSelect field={field} disabled={isDisabled} />;
    case "area":
      return <RenderArea field={field} disabled={isDisabled} />;
    case "integer":
    case "float":
    case "date":
    default:
      return (
        <RenderInput
          validator={field.validator}
          disabled={isDisabled}
          field={field}
        />
      );
  }
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function RenderDocumentConfigs({
  configs,
}: {
  configs: TDocumentConfig[];
}) {
  const { getValues } = useFormContext();
  const fieldValue = getValues("is_there_a_construction");
  const rlApplicationType = getValues("rl_application_type");
  console.log("get field", fieldValue)
  console.log("configs", configs);
  if (!configs.length) return null;

  return (
    <div className="mx-auto max-w-[1000px]">
      <table className="table-fixed w-full">
        <thead className="border-b border-dashed">
          <tr>
            <th className="w-[60%] p-3 text-xl border-r">Document Name</th>
            <th className="w-[40%] p-3 text-xl text-center">Upload</th>
          </tr>
        </thead>
        <tbody>
          {/*
            BEFORE - the filtered documents were listed in document-id order:
              {configs
                .filter((config) => { ... })
                .map((config) => ( ... ))}
            They are now sorted into DOCUMENT_DISPLAY_ORDER after filtering,
            the same order the reviewer screens use.
          */}
          {sortByDocumentDisplayOrder(
            configs.filter((config) => {
              // Show the document Building Plan if fieldValue is true
              if (config.document_config_id === 1) {
                return fieldValue === true;
              }
              // Show the document Site Plan if fieldValue is false
              if (config.document_config_id === 2) {
                return fieldValue === false;
              }
              /**
               * Documents asked for only on an Offline Tentative application
               * are hidden for every other type. This runs before the
               * Permanent-only check below because Registry Certificate is in
               * both lists, and a plain Permanent application must not be
               * asked for it.
               */
              if (OFFLINE_TENTATIVE_ONLY_DOCUMENT_IDS.includes(config.document_config_id)) {
                return isOfflineTentativeTypeValue(rlApplicationType);
              }
              // These documents (K Prat, Registry Certificate, Land Possession
              // Certificate, Ownership Document, Latest Tax Receipt, Color
              // Photo of Plot, L.S. Sketch Map, Additional Affidavit) are only
              // required for Permanent RL applications.
              if ((PERMANENT_ONLY_DOCUMENT_IDS as readonly number[]).includes(config.document_config_id)) {
                const strAppType = String(rlApplicationType ?? "").trim().toLowerCase();
                const isPermanent =
                  rlApplicationType === RL_APPLICATION_TYPE_OPTION_ID.PERMANENT ||
                  rlApplicationType === RL_APPLICATION_TYPE.PERMANENT ||
                  strAppType === "9993" ||
                  strAppType === "permanent";
                return isPermanent;
              }
              // By default, return all other documents
              return true;
            }),
            (config) => config.document_config_id
          )
            // .map((e) => (
            //   <RenderDocumentConfig key={e.document_config_id} config={e} />
            // ))}
            .map((config) => (
              <RenderDocumentConfig
                key={config.document_config_id}
                config={{
                  ...config,
                  mandatory: config.document_config_id === 1 || config.document_config_id === 2 ? 1 : config.mandatory
                }}
              />
            ))}
        </tbody>
      </table>
      <p style={{ color: 'gray', fontStyle: 'italic', marginTop: '15px', marginBottom: '15px', textAlign: 'center' }}>
        Note: Please upload clear and legible documents
      </p>
    </div>
  );
}

export function RenderDocumentConfig({ config }: { config: TDocumentConfig }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const uploadMutation = useUploadFile();
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
  } | null>(null);
  const document = useController({
    name: `documents.${config.document_config_id}`,
    rules: { required: config.mandatory ? "* Required" : false },
  });
  const getDocumentLabel = useGetDocumentLabel();
  const onUpload = async (file: File) => {
    try {
      if (config.file_type) {
        const types = config.file_type.split(",").filter((e) => !!e);
        if (!types.some((e) => file.type.includes(e))) {
          return ErrorToast(
            "Invalid file select, please select a different file!"
          );
        }
      }
      if (
        config.file_size &&
        ["KB", "MB"].includes(config?.file_size_unit || "")
      ) {
        let size = +config.file_size;
        const sizeInMB = file.size / (1024 * 1024);
        if (config.file_size_unit === "KB") {
          size /= 1024;
        }

        if (sizeInMB > size) {
          return ErrorToast(
            `Please select a small file, file cannot be more than ${config.file_size} ${config.file_size_unit}`
          );
        }
      }

      const result = await uploadMutation.mutateAsync(file);
      const url = result?.data?.data;
      if (!url) throw new Error();
      document.field.onChange(url);
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  return (
    <>
      <tr>
        <td className="p-3 border-r">
          <p className="grow text-grey-600">
            {/* {config.document_label} */}
            {getDocumentLabel(config)}
            {config.mandatory ? <span className="text-red-500"> *</span> : null}
          </p>
        </td>
        <td className="flex space-x-6 items-center justify-center p-3 border-0">
          {!document.field?.value ? (
            isMobile ? (
              <div>
                <label
                  htmlFor={document.field.name}
                  className={cx("bg-primary px-4 py-2 rounded block", {
                    ["border-red-500 border"]: document.fieldState?.error,
                  })}
                >
                  <i className="fa fa-upload m-0" />
                  <input
                    type="file"
                    id={document.field.name}
                    name={document.field.name}
                    onChange={(e) => {
                      const hit = e.target.files?.[0];
                      if (hit) onUpload(hit);
                    }}
                  />
                </label>
                {!!document?.fieldState?.error && (
                  <span className="text-xs text-red-500">
                    {document?.fieldState?.error?.message?.toString()}
                  </span>
                )}
              </div>
            ) : (
              <Input
                type="file"
                ref={document.field.ref}
                onBlur={document.field.onBlur}
                disabled={uploadMutation.isPending}
                error={!!document.fieldState?.error}
                onChange={(e) => {
                  const hit = e.target.files?.[0];
                  if (hit) onUpload(hit);
                }}
                helperText={document?.fieldState?.error?.message?.toString()}
                className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
              />
            )
          ) : (
            <>
              <div className="flex justify-center space-x-1">
                <ViewButton
                  onClick={() => {
                    // setPreviewDocument({ url: document.field.value });
                    openInNewTab(document.field.value);
                  }}
                />
                <DeleteButton onClick={() => document.field.onChange(null)} />
                {/* <button onClick={() => openInNewTab(document.field.value)}>
              <i className="text-xl m-0 fa fa-eye"></i>
            </button>
            <button onClick={() => document.field.onChange(null)}>
              <i className="text-xl m-0 fa fa-trash-o"></i>
            </button> */}
              </div>
            </>
          )}
        </td>
      </tr>
      <DocumentPreviewDialog
        onClose={() => setPreviewDocument(null)}
        open={!!previewDocument}
      >
        {previewDocument &&
          previewDocument.url &&
          (previewDocument.url.endsWith("pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={previewDocument.url} />
            </Worker>
          ) : (
            <img
              src={previewDocument.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          ))}
      </DocumentPreviewDialog>
    </>
  );
}
